import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  FileText,
  Send,
  Download,
  Mail,
  Printer,
} from 'lucide-react';

import {
  fetchPaymentRequest,
  PaymentRequest,
} from '../../services/PaymentService';

const PaymentRequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadPaymentRequest(id);
    }
  }, [id]);

  const loadPaymentRequest = async (requestId: string) => {
    try {
      setLoading(true);
      const request = await fetchPaymentRequest(requestId);
      setPaymentRequest(request);
    } catch (error) {
      setError('Failed to load payment request details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (theme === 'dark') {
      switch (status) {
        case 'pending':
          return 'text-yellow-300 bg-yellow-900/30 border-yellow-800';
        case 'paid':
          return 'text-green-300 bg-green-900/30 border-green-800';
        case 'overdue':
          return 'text-red-300 bg-red-900/30 border-red-800';
        case 'cancelled':
          return 'text-gray-300 bg-gray-800 border-gray-700';
        default:
          return 'text-gray-300 bg-gray-800 border-gray-700';
      }
    } else {
      switch (status) {
        case 'pending':
          return 'text-yellow-700 bg-yellow-100 border-yellow-200';
        case 'paid':
          return 'text-green-700 bg-green-100 border-green-200';
        case 'overdue':
          return 'text-red-700 bg-red-100 border-red-200';
        case 'cancelled':
          return 'text-gray-700 bg-gray-100 border-gray-200';
        default:
          return 'text-gray-700 bg-gray-100 border-gray-200';
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5" />;
      case 'paid':
        return <CheckCircle className="w-5 h-5" />;
      case 'overdue':
        return <AlertCircle className="w-5 h-5" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getDaysUntilDue = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const handleResendRequest = async () => {
    // Implementation for resending the payment request
    alert('Payment request resent to client');
  };

  const handleCancelRequest = async () => {
    // Implementation for cancelling the payment request
    alert('Payment request cancelled');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div
          className={`h-16 ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
          } rounded-lg animate-pulse`}
        ></div>
        <div
          className={`${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          } p-6 rounded-lg shadow-sm border animate-pulse`}
        >
          <div
            className={`h-8 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded w-1/3 mb-4`}
          ></div>
          <div
            className={`h-4 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded w-full mb-2`}
          ></div>
          <div
            className={`h-4 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded w-2/3`}
          ></div>
        </div>
      </div>
    );
  }

  if (!paymentRequest) {
    return (
      <div className="text-center py-12">
        <AlertCircle
          className={`w-16 h-16 ${
            theme === 'dark' ? 'text-gray-700' : 'text-gray-300'
          } mx-auto mb-4`}
        />
        <h3
          className={`text-lg font-medium ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          } mb-2`}
        >
          Payment request not found
        </h3>
        <p
          className={`${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          } mb-6`}
        >
          The payment request you're looking for doesn't exist.
        </p>
        <Link
          to="/dashboard/admin/payment-requests"
          className={`inline-flex items-center px-4 py-2 ${
            theme === 'dark'
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white rounded-lg transition-colors duration-200`}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Payment Requests
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/admin/payment-requests"
            className={`p-2 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } rounded-lg transition-colors duration-200`}
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1
              className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              Payment Request Details
            </h1>
            <p
              className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              } mt-1`}
            >
              View and manage payment request
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {paymentRequest.status === 'pending' && (
            <>
              <button
                onClick={handleResendRequest}
                className={`flex items-center px-4 py-2 ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                } text-white rounded-lg transition-colors duration-200`}
              >
                <Send className="w-4 h-4 mr-2" />
                Resend
              </button>
              <button
                onClick={handleCancelRequest}
                className={`flex items-center px-4 py-2 ${
                  theme === 'dark'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-red-600 hover:bg-red-700'
                } text-white rounded-lg transition-colors duration-200`}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel
              </button>
            </>
          )}
          {paymentRequest.status === 'paid' && (
            <button
              className={`flex items-center px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-green-600 hover:bg-green-700'
              } text-white rounded-lg transition-colors duration-200`}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Receipt
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          className={`${
            theme === 'dark'
              ? 'bg-red-900/20 border-red-800'
              : 'bg-red-50 border-red-200'
          } border rounded-lg p-4`}
        >
          <div className="flex items-center">
            <AlertCircle
              className={`w-5 h-5 ${
                theme === 'dark' ? 'text-red-400' : 'text-red-500'
              } mr-2`}
            />
            <span
              className={`${
                theme === 'dark' ? 'text-red-300' : 'text-red-700'
              } text-sm`}
            >
              {error}
            </span>
          </div>
        </div>
      )}

      {/* Payment Request Details */}
      <div
        className={`${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } rounded-lg shadow-sm border`}
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <DollarSign
                className={`w-8 h-8 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`}
              />
              <div>
                <h2
                  className={`text-xl font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {paymentRequest.description}
                </h2>
                <p
                  className={`${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Project: {paymentRequest.projectTitle}
                </p>
              </div>
            </div>
            <span
              className={`flex items-center px-3 py-1 text-sm font-medium rounded-full capitalize border ${getStatusColor(
                paymentRequest.status
              )}`}
            >
              {getStatusIcon(paymentRequest.status)}
              <span className="ml-2">{paymentRequest.status}</span>
            </span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Payment Information */}
          <div>
            <h3
              className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}
            >
              Payment Information
            </h3>
            <div
              className={`${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
              } rounded-lg p-4 space-y-4`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p
                    className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Amount
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {formatCurrency(
                      paymentRequest.amount,
                      paymentRequest.currency
                    )}
                  </p>
                </div>

                <div>
                  <p
                    className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Due Date
                  </p>
                  <p
                    className={`font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {formatDate(paymentRequest.dueDate)}
                  </p>
                  <p
                    className={`text-sm ${
                      new Date(paymentRequest.dueDate) < new Date() &&
                      paymentRequest.status !== 'paid'
                        ? theme === 'dark'
                          ? 'text-red-400'
                          : 'text-red-600'
                        : theme === 'dark'
                        ? 'text-gray-400'
                        : 'text-gray-600'
                    }`}
                  >
                    {getDaysUntilDue(paymentRequest.dueDate)}
                  </p>
                </div>
              </div>

              {paymentRequest.status === 'paid' && (
                <div
                  className={`${
                    theme === 'dark'
                      ? 'bg-green-900/20 border-green-800'
                      : 'bg-green-50 border-green-200'
                  } border rounded-lg p-3`}
                >
                  <div className="flex items-center space-x-2">
                    <CheckCircle
                      className={`w-5 h-5 ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-600'
                      }`}
                    />
                    <div>
                      <p
                        className={`font-medium ${
                          theme === 'dark' ? 'text-green-300' : 'text-green-700'
                        }`}
                      >
                        Paid on {formatDate(paymentRequest.paidAt || '')}
                      </p>
                      <p
                        className={`text-sm ${
                          theme === 'dark' ? 'text-green-400' : 'text-green-600'
                        }`}
                      >
                        Transaction ID: {paymentRequest.transactionId}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Client Information */}
          <div>
            <h3
              className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}
            >
              Client Information
            </h3>
            <div
              className={`${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
              } rounded-lg p-4`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                  }`}
                >
                  <Building2
                    className={`w-5 h-5 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  />
                </div>
                <div>
                  <p
                    className={`font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {paymentRequest.clientName}
                  </p>
                  <p
                    className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Client ID:{' '}
                    {typeof paymentRequest.clientId === 'string'
                      ? paymentRequest.clientId
                      : `${paymentRequest?.clientId?.firstName} ${paymentRequest?.clientId?.lastName}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Request Details */}
          <div>
            <h3
              className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}
            >
              Request Details
            </h3>
            <div
              className={`${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
              } rounded-lg p-4 space-y-3`}
            >
              <div>
                <p
                  className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Description
                </p>
                <p
                  className={`${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {paymentRequest.description}
                </p>
              </div>

              <div>
                <p
                  className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Created By
                </p>
                <p
                  className={`${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {typeof paymentRequest.createdBy === 'string' ||
                  !paymentRequest.createdBy
                    ? paymentRequest.createdBy || 'N/A'
                    : `${paymentRequest.createdBy.firstName} ${paymentRequest.createdBy.lastName}`}
                </p>
              </div>

              <div>
                <p
                  className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Created On
                </p>
                <p
                  className={`${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {formatDate(paymentRequest.createdAt)} (
                  {getRelativeTime(paymentRequest.createdAt)})
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className={`flex items-center justify-between pt-6 border-t ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center space-x-4">
              <button
                className={`flex items-center px-4 py-2 ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } rounded-lg transition-colors duration-200`}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Receipt
              </button>
              <button
                className={`flex items-center px-4 py-2 ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } rounded-lg transition-colors duration-200`}
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>
            </div>

            <Link
              to={`/dashboard/projects/${paymentRequest.projectId}`}
              className={`flex items-center px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white rounded-lg transition-colors duration-200`}
            >
              <FileText className="w-4 h-4 mr-2" />
              View Project
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestDetails;
